import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { AspectRatioBox, Box, Button, prefixUrl } from '@miro-site/core';
import NextImage from 'next/legacy/image';
import { initiatePreproductBoardAction, initiatePreproductBoardEvent, usePreproductPreviewContext, Board } from '@miro-site/preproduct-preview';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var previewVisibleStyles = css(["transition:opacity 0.25s linear;opacity:1;"]);
var ButtonContainer = styled(Box).withConfig({
  displayName: "EditableTemplatePreview__ButtonContainer",
  componentId: "sc-1ffe05-0"
})(["width:100%;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);display:flex;flexflow:column;alignitems:center;color:", ";"], function (_ref) {
  var theme = _ref.theme;
  return theme.colors.primaryWhite;
});
var PreviewContainer = styled(Box).attrs({
  $styles: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    transition: 'opacity 0.25s linear',
    opacity: 0,
    background: 'rgba(var(--wcds-colors-black-rgb), 0.8)'
  }
}).withConfig({
  displayName: "EditableTemplatePreview__PreviewContainer",
  componentId: "sc-1ffe05-1"
})(["&:focus-within{", "}"], previewVisibleStyles);
var EmbedContainer = styled(AspectRatioBox).withConfig({
  displayName: "EditableTemplatePreview__EmbedContainer",
  componentId: "sc-1ffe05-2"
})(["cursor:pointer;padding-top:48.66%;&:hover ", "{", "}"], PreviewContainer, previewVisibleStyles);
var TermsTextContainer = styled.div.withConfig({
  displayName: "EditableTemplatePreview__TermsTextContainer",
  componentId: "sc-1ffe05-3"
})(["margin-top:16px;"]);
var TermsText = styled.span.withConfig({
  displayName: "EditableTemplatePreview__TermsText",
  componentId: "sc-1ffe05-4"
})(["margin-top:16px;font-style:normal;font-weight:400;font-size:12px;text-align:center;letter-spacing:0.003px;color:", ";"], function (_ref2) {
  var theme = _ref2.theme;
  return theme.colors.primaryWhite;
});
var TermsLink = styled(TermsText).withConfig({
  displayName: "EditableTemplatePreview__TermsLink",
  componentId: "sc-1ffe05-5"
})(["text-decoration:underline;"]);
var BoardContainer = styled.div.withConfig({
  displayName: "EditableTemplatePreview__BoardContainer",
  componentId: "sc-1ffe05-6"
})(["position:absolute;top:0;right:0;bottom:0;left:0;"]);

var EditableTemplatePreview = function EditableTemplatePreview() {
  var _useState = useState(false),
      isEmbedVisible = _useState[0],
      setEmbedVisible = _useState[1];

  var _useState2 = useState({}),
      previewStyles = _useState2[0],
      setPreviewStyles = _useState2[1];

  var _usePreproductPreview = usePreproductPreviewContext(),
      dispatch = _usePreproductPreview.dispatch,
      iframeState = _usePreproductPreview.iframeState,
      boardContainerState = _usePreproductPreview.boardContainerState;

  var _boardContainerState$ = boardContainerState.editableTemplates,
      isEditableTemplateBoardActive = _boardContainerState$.isEditableTemplateBoardActive,
      image = _boardContainerState$.editableTemplatePreview;
  useEffect(function () {
    setPreviewStyles({
      display: isEmbedVisible || isEditableTemplateBoardActive ? 'none' : 'block'
    });
  }, [isEmbedVisible, isEditableTemplateBoardActive]);

  var handleOpenTerms = function handleOpenTerms(e) {
    e.stopPropagation();
    window.open('https://miro.com/legal/terms-of-service/', '_blank');
  };

  var handleOpenCookieSettings = function handleOpenCookieSettings(e) {
    e.stopPropagation();
    OneTrust.ToggleInfoDisplay();
  };

  var handleEmbedVisible = function handleEmbedVisible() {
    setEmbedVisible(true);
    initiatePreproductBoardAction(dispatch);
  };

  var handleBoardPreload = function handleBoardPreload() {
    if (iframeState.isIframeReady) {
      initiatePreproductBoardEvent(iframeState.iframeRef, false);
    }
  };

  var handleBoardInitiate = function handleBoardInitiate() {
    if (iframeState.isIframeReady) {
      initiatePreproductBoardEvent(iframeState.iframeRef, true);
      handleEmbedVisible();
      OneTrust.AllowAll();
    }
  };

  return /*#__PURE__*/_jsxs(EmbedContainer, {
    "data-testid": "board-embed-preview-container",
    $width: 720,
    $height: 480,
    onClick: handleBoardInitiate,
    onMouseOver: handleBoardPreload,
    children: [/*#__PURE__*/_jsx(BoardContainer, {
      children: /*#__PURE__*/_jsx(Board, {})
    }), /*#__PURE__*/_jsxs("div", {
      style: previewStyles,
      "data-testid": "board-embed-preview-image-container",
      children: [/*#__PURE__*/_jsx(NextImage, {
        src: prefixUrl(image.original.webp || image.original.jpg),
        unoptimized: true,
        alt: image.name,
        priority: true,
        layout: "fill"
      }), /*#__PURE__*/_jsx(PreviewContainer, {
        children: /*#__PURE__*/_jsxs(ButtonContainer, {
          $styles: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center'
          },
          children: [/*#__PURE__*/_jsx(Button, {
            $buttonType: "primary",
            $size: "medium",
            onClick: handleBoardInitiate,
            children: /*#__PURE__*/_jsx(Box, {
              $styles: {
                display: 'flex',
                flexFlow: 'row nowrap',
                alignItems: 'center'
              },
              children: iframeState.isIframeReady ? 'Try it out live' : 'Loading...'
            })
          }), /*#__PURE__*/_jsxs(TermsTextContainer, {
            children: [/*#__PURE__*/_jsx(TermsText, {
              children: "By trying it out you accept our "
            }), /*#__PURE__*/_jsx(TermsLink, {
              onClick: handleOpenTerms,
              children: "terms of service"
            }), /*#__PURE__*/_jsx(TermsText, {
              children: " and "
            }), /*#__PURE__*/_jsx(TermsLink, {
              onClick: handleOpenCookieSettings,
              children: "cookie settings"
            })]
          })]
        })
      })]
    })]
  });
};

export default EditableTemplatePreview;